import React, { useState, useEffect, useCallback, useRef } from 'react';
import StrictDateInput from '../components/StrictDateInput';
import { formatDateDMY } from '../utils/dateFormat';
import Layout from '../components/Layout';
import { deliveryNoteAPI, productAPI, customerAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

const TAX_METHODS = ['exclusive', 'inclusive'];
const STATUSES = ['active', 'dispatched', 'delivered', 'cancelled'];
const fmtDate = d => formatDateDMY(d, '');
const fmtNum = v => (parseFloat(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const emptyItem = () => ({ product_id: '', product_name: '', item_serial_no: '', quantity: 1, unit: 'Pcs', price: 0, discount: 0, cgst_rate: 0, sgst_rate: 0, igst_rate: 0, cess_rate: 0, mrp: null, description: '', total: 0, alt_unit: '', alt_qty: '' });

/* ── Delivery Note Modal ── */
const DeliveryNoteModal = ({ note, onClose, onSaved }) => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const cardBg = darkMode ? '#1E293B' : '#fff';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const lbl = { color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const secHdr = { fontSize: 13, fontWeight: 800, padding: '8px 0', borderBottom: `1px solid ${border}`, marginBottom: 12, color: darkMode ? '#F1F5F9' : '#0F172A' };

  const [form, setForm] = useState({
    note_type: note?.note_type || 'delivery',
    note_date: note?.note_date ? note.note_date.split('T')[0] : new Date().toISOString().split('T')[0],
    customer_id: note?.customer_id || '',
    customer_name: note?.customer_name || '',
    customer_mobile: note?.customer_mobile || '',
    customer_address: note?.customer_address || '',
    customer_gstin: note?.customer_gstin || '',
    customer_state: note?.customer_state || '',
    discount_amount: note?.discount_amount ?? 0,
    shipping_amount: note?.shipping_amount ?? 0,
    round_off: note?.round_off ?? 0,
    remarks: note?.remarks || '',
    delivery_terms: note?.delivery_terms || '',
    shipping_date: note?.shipping_date ? note.shipping_date.split('T')[0] : '',
    doc_reference: note?.doc_reference || '',
    doc_reference_no: note?.doc_reference_no || '',
    tax_method: note?.tax_method || 'exclusive',
    shipping_tax_rate: note?.shipping_tax_rate ?? 0,
    shipping_cess_rate: note?.shipping_cess_rate ?? 0,
    status: note?.status || 'active',
  });
  const [items, setItems] = useState(note?.items?.length ? note.items.map(i => ({ ...emptyItem(), ...i })) : [emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [custSearch, setCustSearch] = useState('');
  const [custResults, setCustResults] = useState([]);
  const [prodSearch, setProdSearch] = useState({});
  const [prodResults, setProdResults] = useState({});
  const custRef = useRef(null);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // Customer search
  useEffect(() => {
    if (!custSearch || custSearch.length < 2) { setCustResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await customerAPI.list({ search: custSearch, page_size: 6 }); setCustResults(r.data.customers || []); } catch { setCustResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [custSearch]);

  const pickCustomer = c => {
    setForm(f => ({ ...f, customer_id: c.id, customer_name: c.name || '', customer_mobile: c.mobile || '', customer_address: c.address || '', customer_gstin: c.gstin || '', customer_state: c.state || '' }));
    setCustSearch(''); setCustResults([]);
  };

  // Product search per row
  const searchProduct = (idx, q) => {
    setProdSearch(s => ({ ...s, [idx]: q }));
    if (!q || q.length < 2) { setProdResults(s => ({ ...s, [idx]: [] })); return; }
    const t = setTimeout(async () => {
      try { const r = await productAPI.list({ search: q, page_size: 6 }); setProdResults(s => ({ ...s, [idx]: r.data.products || [] })); } catch {}
    }, 300);
    return () => clearTimeout(t);
  };

  const pickProduct = (idx, p) => {
    setItems(its => its.map((it, i) => i !== idx ? it : {
      ...it, product_id: p.id, product_name: p.name, price: parseFloat(p.selling_price) || 0,
      mrp: parseFloat(p.mrp) || null, unit: p.unit || 'Pcs',
      cgst_rate: parseFloat(p.cgst_rate) || parseFloat(p.tax_rate) / 2 || 0,
      sgst_rate: parseFloat(p.sgst_rate) || parseFloat(p.tax_rate) / 2 || 0,
      igst_rate: parseFloat(p.igst_rate) || 0, cess_rate: parseFloat(p.cess_rate) || 0,
      total: (parseFloat(p.selling_price) || 0) * 1,
    }));
    setProdSearch(s => ({ ...s, [idx]: '' })); setProdResults(s => ({ ...s, [idx]: [] }));
  };

  const updateItem = (idx, key, val) => {
    setItems(its => its.map((it, i) => {
      if (i !== idx) return it;
      const n = { ...it, [key]: val };
      const qty = parseFloat(n.quantity) || 0;
      const price = parseFloat(n.price) || 0;
      const disc = parseFloat(n.discount) || 0;
      n.total = Math.max(0, qty * price - disc);
      return n;
    }));
  };

  const addItem = () => setItems(its => [...its, emptyItem()]);
  const removeItem = idx => setItems(its => its.length <= 1 ? its : its.filter((_, i) => i !== idx));

  const subtotal = items.reduce((s, it) => s + (parseFloat(it.total) || 0), 0);
  const cgstAmt = items.reduce((s, it) => s + ((parseFloat(it.total) || 0) * (parseFloat(it.cgst_rate) || 0) / 100), 0);
  const sgstAmt = items.reduce((s, it) => s + ((parseFloat(it.total) || 0) * (parseFloat(it.sgst_rate) || 0) / 100), 0);
  const igstAmt = items.reduce((s, it) => s + ((parseFloat(it.total) || 0) * (parseFloat(it.igst_rate) || 0) / 100), 0);
  const cessAmt = items.reduce((s, it) => s + ((parseFloat(it.total) || 0) * (parseFloat(it.cess_rate) || 0) / 100), 0);
  const discount = parseFloat(form.discount_amount) || 0;
  const shipping = parseFloat(form.shipping_amount) || 0;
  const roundOff = parseFloat(form.round_off) || 0;
  const grandTotal = subtotal + cgstAmt + sgstAmt + igstAmt + cessAmt - discount + shipping + roundOff;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.customer_name.trim()) { alert('Customer name is required'); return; }
    if (!items.some(it => it.product_name.trim())) { alert('Add at least one item'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount_amount: discount, shipping_amount: shipping, round_off: roundOff,
        shipping_tax_rate: parseFloat(form.shipping_tax_rate) || 0,
        shipping_cess_rate: parseFloat(form.shipping_cess_rate) || 0,
        subtotal, cgst_amount: cgstAmt, sgst_amount: sgstAmt, igst_amount: igstAmt, cess_amount: cessAmt, grand_total: grandTotal,
        items: items.filter(it => it.product_name.trim()).map(it => ({
          product_id: it.product_id || null, product_name: it.product_name, item_serial_no: it.item_serial_no || null,
          quantity: parseFloat(it.quantity) || 1, unit: it.unit || 'Pcs', price: parseFloat(it.price) || 0,
          discount: parseFloat(it.discount) || 0, cgst_rate: parseFloat(it.cgst_rate) || 0,
          sgst_rate: parseFloat(it.sgst_rate) || 0, igst_rate: parseFloat(it.igst_rate) || 0,
          cess_rate: parseFloat(it.cess_rate) || 0, mrp: it.mrp ? parseFloat(it.mrp) : null,
          description: it.description || null, total: parseFloat(it.total) || 0,
          alt_unit: it.alt_unit || null, alt_qty: it.alt_qty ? parseFloat(it.alt_qty) : null,
        })),
      };
      if (note) await deliveryNoteAPI.update(note.id, payload);
      else await deliveryNoteAPI.create(payload);
      onSaved();
    } catch (err) { alert(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 10 }}>
      <div style={{ background: cardBg, borderRadius: 16, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflow: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)` }} />
        <form onSubmit={handleSubmit} style={{ padding: 24 }}>
          <h3 style={{ color: darkMode ? '#F1F5F9' : '#0F172A', fontSize: 18, fontWeight: 800, margin: '0 0 20px' }}>
            {note ? '✏️ Edit Delivery Note' : '🚚 New Delivery Note'}
          </h3>

          {/* Customer Section */}
          <div style={secHdr}>👤 Customer</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
            <div style={{ position: 'relative' }} ref={custRef}>
              <label style={lbl}>Customer Name *</label>
              <input style={inp} value={form.customer_name} onChange={e => { set('customer_name')(e); setCustSearch(e.target.value); }} placeholder="Search or type name" required />
              {custResults.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: cardBg, border: `1px solid ${border}`, borderRadius: 8, maxHeight: 180, overflow: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                  {custResults.map(c => (
                    <div key={c.id} onClick={() => pickCustomer(c)} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: darkMode ? '#F1F5F9' : '#0F172A', borderBottom: `1px solid ${border}` }}>{c.name} — {c.mobile}</div>
                  ))}
                </div>
              )}
            </div>
            <div><label style={lbl}>Mobile</label><input style={inp} value={form.customer_mobile} onChange={set('customer_mobile')} /></div>
            <div><label style={lbl}>GSTIN</label><input style={inp} value={form.customer_gstin} onChange={set('customer_gstin')} /></div>
            <div><label style={lbl}>State</label><input style={inp} value={form.customer_state} onChange={set('customer_state')} /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Address</label><input style={inp} value={form.customer_address} onChange={set('customer_address')} /></div>
          </div>

          {/* Note Details */}
          <div style={secHdr}>📋 Note Details</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
            <div><label style={lbl}>Note Date</label><StrictDateInput style={inp}  value={form.note_date} onChange={set('note_date')} /></div>
            <div><label style={lbl}>Note Type</label><input style={inp} value={form.note_type} onChange={set('note_type')} /></div>
            <div><label style={lbl}>Tax Method</label>
              <select style={inp} value={form.tax_method} onChange={set('tax_method')}>
                {TAX_METHODS.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Status</label>
              <select style={inp} value={form.status} onChange={set('status')}>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Shipping Date</label><StrictDateInput style={inp}  value={form.shipping_date} onChange={set('shipping_date')} /></div>
            <div><label style={lbl}>Delivery Terms</label><input style={inp} value={form.delivery_terms} onChange={set('delivery_terms')} /></div>
            <div><label style={lbl}>Doc Reference</label><input style={inp} value={form.doc_reference} onChange={set('doc_reference')} /></div>
            <div><label style={lbl}>Doc Reference No.</label><input style={inp} value={form.doc_reference_no} onChange={set('doc_reference_no')} /></div>
          </div>

          {/* Items Table */}
          <div style={secHdr}>📦 Items</div>
          <div style={{ overflowX: 'auto', marginBottom: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900, fontSize: 13 }}>
              <thead>
                <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                  {['#', 'Product', 'HSN/Unit', 'Qty', 'Price', 'Disc', 'CGST%', 'SGST%', 'Total', ''].map(h => (
                    <th key={h} style={{ padding: '8px 6px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: darkMode ? '#64748B' : '#94A3B8', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx} style={{ borderTop: `1px solid ${border}` }}>
                    <td style={{ padding: '6px', color: darkMode ? '#64748B' : '#94A3B8', fontSize: 12 }}>{idx + 1}</td>
                    <td style={{ padding: '4px', position: 'relative', minWidth: 180 }}>
                      <input style={{ ...inp, fontSize: 12, padding: '6px 8px' }} value={prodSearch[idx] ?? it.product_name} onChange={e => { updateItem(idx, 'product_name', e.target.value); searchProduct(idx, e.target.value); }} placeholder="Search product" />
                      {(prodResults[idx] || []).length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: cardBg, border: `1px solid ${border}`, borderRadius: 6, maxHeight: 150, overflow: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                          {prodResults[idx].map(p => (
                            <div key={p.id} onClick={() => pickProduct(idx, p)} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 12, color: darkMode ? '#F1F5F9' : '#0F172A', borderBottom: `1px solid ${border}` }}>{p.name} — ₹{p.selling_price}</div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '4px' }}><input style={{ ...inp, fontSize: 12, padding: '6px 8px', width: 70 }} value={it.unit} onChange={e => updateItem(idx, 'unit', e.target.value)} /></td>
                    <td style={{ padding: '4px' }}><input style={{ ...inp, fontSize: 12, padding: '6px 8px', width: 55 }} type="number" min="0" step="any" value={it.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} /></td>
                    <td style={{ padding: '4px' }}><input style={{ ...inp, fontSize: 12, padding: '6px 8px', width: 80 }} type="number" min="0" step="0.01" value={it.price} onChange={e => updateItem(idx, 'price', e.target.value)} /></td>
                    <td style={{ padding: '4px' }}><input style={{ ...inp, fontSize: 12, padding: '6px 8px', width: 60 }} type="number" min="0" step="0.01" value={it.discount} onChange={e => updateItem(idx, 'discount', e.target.value)} /></td>
                    <td style={{ padding: '4px' }}><input style={{ ...inp, fontSize: 12, padding: '6px 8px', width: 50 }} type="number" min="0" step="0.01" value={it.cgst_rate} onChange={e => updateItem(idx, 'cgst_rate', e.target.value)} /></td>
                    <td style={{ padding: '4px' }}><input style={{ ...inp, fontSize: 12, padding: '6px 8px', width: 50 }} type="number" min="0" step="0.01" value={it.sgst_rate} onChange={e => updateItem(idx, 'sgst_rate', e.target.value)} /></td>
                    <td style={{ padding: '6px', fontWeight: 700, color: darkMode ? '#F1F5F9' : '#0F172A' }}>₹{fmtNum(it.total)}</td>
                    <td style={{ padding: '4px' }}>
                      <button type="button" onClick={() => removeItem(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }} title="Remove">🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={addItem} style={{ padding: '6px 14px', background: 'transparent', border: `1px dashed ${border}`, borderRadius: 6, cursor: 'pointer', color: acc, fontWeight: 600, fontSize: 13, marginBottom: 16 }}>+ Add Item</button>

          {/* Charges */}
          <div style={secHdr}>💰 Charges & Totals</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div><label style={lbl}>Discount (₹)</label><input style={inp} type="number" min="0" step="0.01" value={form.discount_amount} onChange={set('discount_amount')} /></div>
            <div><label style={lbl}>Shipping (₹)</label><input style={inp} type="number" min="0" step="0.01" value={form.shipping_amount} onChange={set('shipping_amount')} /></div>
            <div><label style={lbl}>Shipping Tax %</label><input style={inp} type="number" min="0" step="0.01" value={form.shipping_tax_rate} onChange={set('shipping_tax_rate')} /></div>
            <div><label style={lbl}>Shipping Cess %</label><input style={inp} type="number" min="0" step="0.01" value={form.shipping_cess_rate} onChange={set('shipping_cess_rate')} /></div>
            <div><label style={lbl}>Round Off (₹)</label><input style={inp} type="number" step="0.01" value={form.round_off} onChange={set('round_off')} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 4, textAlign: 'right', marginBottom: 16, fontSize: 13, color: darkMode ? '#94A3B8' : '#64748B' }}>
            <div>Subtotal: <strong style={{ color: darkMode ? '#F1F5F9' : '#0F172A' }}>₹{fmtNum(subtotal)}</strong></div>
            {cgstAmt > 0 && <div>CGST: +₹{fmtNum(cgstAmt)}</div>}
            {sgstAmt > 0 && <div>SGST: +₹{fmtNum(sgstAmt)}</div>}
            {igstAmt > 0 && <div>IGST: +₹{fmtNum(igstAmt)}</div>}
            {cessAmt > 0 && <div>Cess: +₹{fmtNum(cessAmt)}</div>}
            {discount > 0 && <div>Discount: −₹{fmtNum(discount)}</div>}
            {shipping > 0 && <div>Shipping: +₹{fmtNum(shipping)}</div>}
            {roundOff !== 0 && <div>Round Off: {roundOff >= 0 ? '+' : ''}₹{fmtNum(roundOff)}</div>}
            <div style={{ fontSize: 16, fontWeight: 800, color: acc, marginTop: 4 }}>Grand Total: ₹{fmtNum(grandTotal)}</div>
          </div>

          {/* Remarks */}
          <div style={{ marginBottom: 16 }}><label style={lbl}>Remarks</label><textarea style={{ ...inp, minHeight: 50, resize: 'vertical' }} value={form.remarks} onChange={set('remarks')} /></div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '10px 20px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', color: darkMode ? '#94A3B8' : '#64748B' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '10px 24px', background: saving ? '#94A3B8' : acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? '⏳ Saving…' : note ? '💾 Update' : '💾 Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Main Delivery Notes Page ── */
const DeliveryNotes = () => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none' };

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      const r = await deliveryNoteAPI.list(params);
      const data = r.data;
      setNotes(data.delivery_notes || data.notes || []);
      const total = data.total || 0;
      setTotalPages(Math.ceil(total / 20) || 1);
    } catch { setNotes([]); }
    setLoading(false);
  }, [page, search, filterStatus]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const openEdit = async n => {
    try { const r = await deliveryNoteAPI.get(n.id); setEditNote(r.data); setShowModal(true); } catch { setEditNote(n); setShowModal(true); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await deliveryNoteAPI.delete(deleteId); setDeleteId(null); fetchNotes(); } catch { alert('Delete failed'); }
  };

  return (
    <Layout>
      <div style={{ padding: 'clamp(12px, 3vw, 28px)', maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ ...pg, fontSize: 22, fontWeight: 800, margin: 0 }}>🚚 Delivery Notes</h1>
            <p style={{ ...muted, fontSize: 13, margin: '4px 0 0' }}>Create and manage delivery challans</p>
          </div>
          <button onClick={() => { setEditNote(null); setShowModal(true); }}
            style={{ padding: '10px 20px', background: acc, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
            ➕ New Delivery Note
          </button>
        </div>

        {/* Filters */}
        <div style={{ ...card, padding: 14, marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input style={{ ...inp, flex: 1, minWidth: 200 }} value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="🔍 Search delivery notes…" />
          <select style={{ ...inp, width: 150 }} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        {/* Table */}
        <div style={{ ...card, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', ...muted }}>Loading…</div>
          ) : notes.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🚚</div>
              <div style={{ ...pg, fontWeight: 700 }}>No delivery notes found</div>
              <div style={{ ...muted, fontSize: 13 }}>Create your first delivery note</div>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                  <thead>
                    <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                      {['Note #', 'Date', 'Customer', 'Items', 'Total', 'Status', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: darkMode ? '#64748B' : '#94A3B8', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {notes.map(n => (
                      <tr key={n.id} style={{ borderTop: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                        <td style={{ padding: '10px 14px', fontWeight: 700, ...pg, fontSize: 13 }}>{n.note_number}</td>
                        <td style={{ padding: '10px 14px', fontSize: 13, ...pg }}>{fmtDate(n.note_date)}</td>
                        <td style={{ padding: '10px 14px', fontSize: 13, ...pg }}>{n.customer_name}</td>
                        <td style={{ padding: '10px 14px', fontSize: 13, ...muted }}>{n.item_count || '—'}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 700, ...pg, fontSize: 13 }}>₹{fmtNum(n.grand_total)}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                            background: n.status === 'delivered' ? '#dcfce7' : n.status === 'dispatched' ? '#fef3c7' : n.status === 'cancelled' ? '#fee2e2' : (darkMode ? '#1e3a5f' : '#eff6ff'),
                            color: n.status === 'delivered' ? '#16a34a' : n.status === 'dispatched' ? '#d97706' : n.status === 'cancelled' ? '#dc2626' : acc
                          }}>{n.status}</span>
                        </td>
                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                          <button onClick={() => openEdit(n)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, marginRight: 6 }} title="Edit">✏️</button>
                          <button onClick={() => setDeleteId(n.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }} title="Delete">🗑️</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div style={{ padding: '12px 14px', display: 'flex', justifyContent: 'center', gap: 8 }}>
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ padding: '6px 14px', background: darkMode ? '#0F172A' : '#F8FAFC', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 6, cursor: page <= 1 ? 'not-allowed' : 'pointer', ...pg }}>← Prev</button>
                  <span style={{ ...muted, fontSize: 13, padding: '6px 10px' }}>Page {page} of {totalPages}</span>
                  <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ padding: '6px 14px', background: darkMode ? '#0F172A' : '#F8FAFC', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 6, cursor: page >= totalPages ? 'not-allowed' : 'pointer', ...pg }}>Next →</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && <DeliveryNoteModal note={editNote} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); fetchNotes(); }} />}

      {/* Delete Confirmation */}
      {deleteId && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: darkMode ? '#1E293B' : '#fff', borderRadius: 14, padding: 24, maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', width: '100%', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>⚠️</div>
            <h3 style={{ ...pg, marginBottom: 8 }}>Delete Delivery Note?</h3>
            <p style={{ ...muted, fontSize: 13, marginBottom: 20 }}>This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => setDeleteId(null)} style={{ padding: '9px 20px', background: 'transparent', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, cursor: 'pointer', ...pg }}>Cancel</button>
              <button onClick={handleDelete} style={{ padding: '9px 20px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default DeliveryNotes;
